import * as React from "react";
import * as HoverCardPrimitive from "@radix-ui/react-hover-card";
import { cva } from "class-variance-authority";
import { ArrowUpRight, TrendingUp, TrendingDown, ChevronRight, X } from "lucide-react";
import * as SheetPrimitive from "@radix-ui/react-dialog";

import { cn } from "../lib/utils";

const HoverCardContext = React.createContext({
  isTouch: false,
  open: false,
  setOpen: () => {},
});

function useIsTouch() {
  const [isTouch, setIsTouch] = React.useState(false);

  React.useEffect(() => {
    const mql = window.matchMedia("(hover: none) or (pointer: coarse)");
    setIsTouch(mql.matches);

    const handler = (e) => setIsTouch(e.matches);
    if (mql.addEventListener) {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    }
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, []);

  return isTouch;
}

const HoverCard = ({
  children,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  openDelay = 200,
  closeDelay = 250,
  ...props
}) => {
  const isTouch = useIsTouch();
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;

  const handleOpenChange = React.useCallback(
    (nextOpen) => {
      if (!isControlled) {
        setUncontrolledOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
    },
    [isControlled, onOpenChange]
  );

  const contextValue = React.useMemo(
    () => ({
      isTouch,
      open,
      setOpen: handleOpenChange,
    }),
    [isTouch, open, handleOpenChange]
  );

  if (isTouch) {
    return (
      <HoverCardContext.Provider value={contextValue}>
        <SheetPrimitive.Root open={open} onOpenChange={handleOpenChange} {...props}>
          {children}
        </SheetPrimitive.Root>
      </HoverCardContext.Provider>
    );
  }

  return (
    <HoverCardContext.Provider value={contextValue}>
      <HoverCardPrimitive.Root
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={handleOpenChange}
        openDelay={openDelay}
        closeDelay={closeDelay}
        {...props}
      >
        {children}
      </HoverCardPrimitive.Root>
    </HoverCardContext.Provider>
  );
};
HoverCard.displayName = "HoverCard";

const HoverCardTrigger = React.forwardRef(
  ({ className, asChild = false, children, onClick, ...props }, ref) => {
    const { isTouch, setOpen } = React.useContext(HoverCardContext);

    if (isTouch) {
      const Comp = asChild ? SheetPrimitive.Trigger : "button";
      return (
        <Comp
          ref={ref}
          asChild={asChild}
          className={cn("cursor-pointer", className)}
          onClick={(e) => {
            onClick?.(e);
            setOpen(true);
          }}
          {...props}
        >
          {children}
        </Comp>
      );
    }

    return (
      <HoverCardPrimitive.Trigger
        ref={ref}
        asChild={asChild}
        className={cn("cursor-pointer", className)}
        onClick={onClick}
        {...props}
      >
        {children}
      </HoverCardPrimitive.Trigger>
    );
  }
);
HoverCardTrigger.displayName = "HoverCardTrigger";

const HoverCardContent = React.forwardRef(
  (
    {
      className,
      align = "center",
      side = "top",
      sideOffset = 8,
      collisionPadding = 12,
      children,
      ...props
    },
    ref
  ) => {
    const { isTouch, setOpen } = React.useContext(HoverCardContext);

    if (isTouch) {
      return (
        <SheetPrimitive.Portal>
          <SheetPrimitive.Overlay className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <SheetPrimitive.Content
            ref={ref}
            className={cn(
              "fixed inset-x-0 bottom-0 z-50 flex max-h-[85vh] flex-col rounded-t-modal border-t border-border bg-card p-5 text-foreground shadow-2xl focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
              className
            )}
            {...props}
          >
            {/* Mobile drag affordance pill */}
            <div className="mx-auto -mt-2 mb-3 h-1.5 w-12 rounded-full bg-muted-foreground/30" />
            <SheetPrimitive.Close className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </SheetPrimitive.Close>
            <div className="overflow-y-auto">{children}</div>
          </SheetPrimitive.Content>
        </SheetPrimitive.Portal>
      );
    }

    return (
      <HoverCardPrimitive.Portal>
        <HoverCardPrimitive.Content
          ref={ref}
          align={align}
          side={side}
          sideOffset={sideOffset}
          collisionPadding={collisionPadding}
          className={cn(
            "z-50 w-[340px] rounded-modal border border-border bg-card/95 p-4 text-popover-foreground shadow-2xl backdrop-blur-2xl outline-none",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            className
          )}
          {...props}
        >
          {children}
        </HoverCardPrimitive.Content>
      </HoverCardPrimitive.Portal>
    );
  }
);
HoverCardContent.displayName = "HoverCardContent";

const statusBadgeVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-caption-size font-medium border shrink-0",
  {
    variants: {
      variant: {
        success: "bg-success/15 text-success border-success/30",
        warning: "bg-warning/15 text-warning border-warning/30",
        destructive: "bg-destructive/15 text-destructive border-destructive/30",
        info: "bg-info/15 text-info border-info/30",
        secondary: "bg-muted text-muted-foreground border-border",
      },
    },
    defaultVariants: {
      variant: "secondary",
    },
  }
);

/**
 * High-density 4-tier status card for hover popovers
 */
function StatusDetailCard({
  identity,
  description,
  heroMetric,
  telemetryRows = [],
  badges = [],
  actions = [],
  viewAllHref,
  onViewAll,
  viewAllLabel = "Details",
  className,
}) {
  const IconComp = identity?.icon;
  const safeTelemetryRows = Array.isArray(telemetryRows) ? telemetryRows : [];
  const safeBadges = Array.isArray(badges) ? badges : [];
  const safeActions = Array.isArray(actions) ? actions : [];

  return (
    <div className={cn("flex flex-col gap-3 text-left", className)}>
      {/* Tier 1: Identity Header */}
      <div className="flex items-start justify-between gap-2.5">
        <div className="flex items-start gap-2.5 min-w-0 flex-1">
          {identity?.avatar ? (
            <img
              src={identity.avatar}
              alt={identity.title || ""}
              className={cn(
                "size-10 shrink-0 object-cover border border-border",
                identity.avatarShape === "rounded" ? "rounded-xl" : "rounded-full"
              )}
            />
          ) : IconComp ? (
            <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-muted/50 border border-border text-foreground">
              <IconComp className="size-4" />
            </div>
          ) : null}
          <div className="min-w-0 flex-1">
            <h4 className="line-clamp-2 font-semibold text-sm text-foreground leading-snug tracking-tight">
              {identity?.title}
            </h4>
            {identity?.subtitle && (
              <p className="line-clamp-2 text-xs text-muted-foreground leading-snug mt-0.5">
                {identity.subtitle}
              </p>
            )}
          </div>
        </div>

        {identity?.statusLabel && (
          <span
            className={cn(
              statusBadgeVariants({
                variant: identity.statusVariant || "secondary",
              })
            )}
          >
            <span
              className={cn(
                "size-1.5 rounded-full shrink-0",
                identity.statusVariant === "success" && "bg-success",
                identity.statusVariant === "warning" && "bg-warning",
                identity.statusVariant === "destructive" && "bg-destructive",
                identity.statusVariant === "info" && "bg-info",
                (!identity.statusVariant || identity.statusVariant === "secondary") &&
                  "bg-muted-foreground"
              )}
            />
            {identity.statusLabel}
          </span>
        )}
      </div>

      {/* Optional Description / Notes Context */}
      {description && (
        <p className="line-clamp-2 text-xs text-muted-foreground leading-relaxed rounded-lg bg-muted/20 border border-border/50 px-2.5 py-1.5">
          {description}
        </p>
      )}

      {/* Tier 2: Hero Metric */}
      {heroMetric && (
        <div className="rounded-xl bg-muted/40 border border-border p-2.5 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <span className="block text-overline-size uppercase font-semibold tracking-wider text-muted-foreground">
              {heroMetric.label}
            </span>
            <span className="text-lg font-bold font-mono tabular-nums text-foreground">
              {heroMetric.value}
            </span>
          </div>
          {heroMetric.secondaryValue ? (
            <div className="text-right shrink-0">
              <span className="block text-overline-size uppercase font-semibold tracking-wider text-muted-foreground">
                {heroMetric.secondaryLabel}
              </span>
              <span className="text-sm font-semibold font-mono tabular-nums text-success">
                {heroMetric.secondaryValue}
              </span>
            </div>
          ) : heroMetric.trend ? (
            <div
              className={cn(
                "flex items-center gap-1 text-xs font-medium font-mono tabular-nums",
                heroMetric.trend.value >= 0 ? "text-success" : "text-destructive"
              )}
            >
              {heroMetric.trend.value >= 0 ? (
                <TrendingUp className="size-3.5" />
              ) : (
                <TrendingDown className="size-3.5" />
              )}
              <span>{Math.abs(heroMetric.trend.value)}%</span>
            </div>
          ) : null}
        </div>
      )}

      {/* Tier 3: Secondary Telemetry */}
      {safeTelemetryRows.length > 0 && (
        <div className="grid grid-cols-2 gap-2 text-xs">
          {safeTelemetryRows.map((row, idx) => (
            <div
              key={idx}
              className="rounded-lg bg-muted/20 border border-border/50 p-2"
            >
              <p className="text-overline-size text-muted-foreground truncate">{row.label}</p>
              <p className="font-semibold text-foreground font-mono tabular-nums truncate mt-0.5" title={String(row.value)}>
                {row.value}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Optional Badges / Amenities Row */}
      {safeBadges.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {safeBadges.map((b, idx) => (
            <span
              key={idx}
              className="inline-flex items-center rounded-md bg-muted/40 border border-border px-2 py-0.5 text-caption font-medium text-muted-foreground"
            >
              {typeof b === "string" ? b : b.label}
            </span>
          ))}
        </div>
      )}

      {/* Tier 4: Micro Actions */}
      {(safeActions.length > 0 || viewAllHref || onViewAll) && (
        <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-border">
          <div className="flex items-center gap-1.5 flex-wrap">
            {safeActions.map((action, idx) => {
              const ActionIcon = action.icon;
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onClick?.(e);
                  }}
                  className={cn(
                    "inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg transition-colors cursor-pointer",
                    action.variant === "default"
                      ? "bg-primary text-primary-foreground hover:bg-primary/90"
                      : "bg-muted text-foreground hover:bg-accent border border-border"
                  )}
                >
                  {ActionIcon && <ActionIcon className="size-3" />}
                  <span>{action.label}</span>
                </button>
              );
            })}
          </div>

          {(viewAllHref || onViewAll) && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (onViewAll) onViewAll();
                else if (viewAllHref) window.location.href = viewAllHref;
              }}
              className="inline-flex items-center gap-0.5 text-xs font-medium text-muted-foreground hover:text-primary transition-colors ml-auto cursor-pointer"
            >
              <span>{viewAllLabel}</span>
              <ChevronRight className="size-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
  StatusDetailCard,
  statusBadgeVariants,
};
